import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/utils.js';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Largura em px — default 480. Ignorado se `size` for passado. */
  width?: number;
  /** Tamanho nominal (compat com componentes portados do super.old). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Variante sheet lateral (estilo print MetricsFarma). */
  variant?: 'modal' | 'sheet';
}

const SIZE_PX: Record<'sm' | 'md' | 'lg' | 'xl', number> = {
  sm: 384,
  md: 448,
  lg: 512,
  xl: 576,
};

export function Modal({ open, onClose, title, children, width, size, variant = 'modal' }: ModalProps) {
  const effectiveWidth = size ? SIZE_PX[size] : (width ?? 480);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[60] flex bg-black/30',
        variant === 'modal'
          ? 'items-center justify-center p-4'
          : 'items-stretch justify-end',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'bg-white border border-[#e2e8f0] shadow-xl',
          variant === 'modal'
            ? 'rounded-xl max-h-[90vh] overflow-auto'
            : 'h-screen overflow-auto animate-slide-in-right',
        )}
        style={{ width: `${effectiveWidth}px` }}
      >
        <div className="flex items-center justify-between border-b border-[#f1f5f9] px-6 py-4 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-[#0f172a]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[#94a3b8] hover:text-[#64748b] rounded-lg p-1.5 hover:bg-[#f1f5f9] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
